import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { aiApi, holdingsApi } from '../services/api';

const GENERIC_QUESTIONS = [
  'How is my overall portfolio positioned for income?',
  'What is a covered call ETF?',
  'How can I diversify my dividend income?',
  'What is the difference between yield and total return?',
  'How often should I rebalance my portfolio?',
  'What is dividend reinvestment (DRIP)?',
];

function buildPersonalisedQuestions(holdings) {
  if (!holdings || holdings.length === 0) return GENERIC_QUESTIONS;
  const questions = [];

  // 1. Portfolio-level first
  questions.push('How is my overall portfolio positioned for income growth?');
  questions.push('What is my biggest risk in my current portfolio?');

  // 2. Ticker-specific — top 3 holdings by value
  holdings.slice(0, 3).forEach(h => {
    const yld    = parseFloat(h.dividend_yield);
    const ticker = h.ticker;
    if (yld > 15)      questions.push(`Is ${ticker}'s ${yld.toFixed(1)}% yield a dividend trap?`);
    else if (yld > 8)  questions.push(`Is ${ticker}'s ${yld.toFixed(1)}% yield sustainable long-term?`);
    else if (yld === 0) questions.push(`Should I keep ${ticker} if it pays no dividends?`);
    else               questions.push(`What is the outlook for ${ticker} as a dividend stock?`);
  });

  // 3. Sector-based
  const sectorMap = {};
  holdings.forEach(h => {
    const sec = h.sector || 'Unknown';
    sectorMap[sec] = (sectorMap[sec] || 0) + (parseFloat(h.shares) * parseFloat(h.current_price));
  });
  Object.entries(sectorMap)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2)
    .forEach(([sec]) => questions.push(`Am I too concentrated in the ${sec} sector?`));

  // 4. Pad with generics
  const generic = GENERIC_QUESTIONS.filter(q => !questions.includes(q));
  while (questions.length < 6 && generic.length) questions.push(generic.shift());

  return questions.filter(Boolean);
}

function pickSuggestions(pool, count = 3, exclude = []) {
  return pool.filter(q => !exclude.includes(q)).slice(0, count);
}

// ── Simple markdown renderer ──────────────────────────────────────────────────
function renderMarkdown(text) {
  const lines = text.split('\n');
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Blank line
    if (line.trim() === '') { i++; continue; }

    // Bullet list item
    if (/^[\-\*•]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^[\-\*•]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^[\-\*•]\s+/, ''));
        i++;
      }
      elements.push(
        <ul key={i} className="list-none space-y-1 my-1">
          {items.map((item, j) => (
            <li key={j} className="flex items-start gap-2">
              <span className="text-violet-400 mt-0.5 flex-shrink-0">•</span>
              <span>{inlineFormat(item)}</span>
            </li>
          ))}
        </ul>
      );
      continue;
    }

    // Numbered list
    if (/^\d+\.\s+/.test(line)) {
      const items = [];
      let num = 1;
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s+/, ''));
        i++;
      }
      elements.push(
        <ol key={i} className="list-none space-y-1 my-1">
          {items.map((item, j) => (
            <li key={j} className="flex items-start gap-2">
              <span className="text-violet-500 font-semibold flex-shrink-0 w-4">{j + 1}.</span>
              <span>{inlineFormat(item)}</span>
            </li>
          ))}
        </ol>
      );
      continue;
    }

    // Regular paragraph
    elements.push(<p key={i} className="mb-1">{inlineFormat(line)}</p>);
    i++;
  }

  return elements;
}

// Handle **bold**, *italic*, and `code` inline
function inlineFormat(text) {
  const parts = [];
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`)/g;
  let last = 0, match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    if (match[2]) parts.push(<strong key={match.index} className="font-semibold">{match[2]}</strong>);
    else if (match[3]) parts.push(<em key={match.index}>{match[3]}</em>);
    else if (match[4]) parts.push(<code key={match.index} className="bg-gray-100 text-violet-700 rounded px-1 text-xs font-mono">{match[4]}</code>);
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? parts : text;
}

// ── Sub-components ────────────────────────────────────────────────────────────
function TypingIndicator() {
  return (
    <div className="flex items-end gap-2 mb-4">
      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0 shadow">
        <BotIcon className="w-4 h-4 text-white" />
      </div>
      <div className="bg-white border border-gray-100 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
        <div className="flex gap-1 items-center h-4">
          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  );
}

function Message({ msg, suggestions, onSuggest }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex items-end gap-2 mb-1 ${isUser ? 'flex-row-reverse' : ''}`}>
      {!isUser && (
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0 shadow self-start mt-1">
          <BotIcon className="w-4 h-4 text-white" />
        </div>
      )}

      <div className="flex flex-col gap-2 max-w-[82%]">
        <div className={`px-4 py-3 rounded-2xl text-sm leading-relaxed shadow-sm ${
          isUser
            ? 'bg-gradient-to-br from-violet-600 to-purple-700 text-white rounded-br-sm'
            : 'bg-white border border-gray-100 text-gray-800 rounded-bl-sm'
        }`}>
          {isUser ? msg.content : renderMarkdown(msg.content)}
        </div>

        {/* Follow-up suggestions below AI reply */}
        {!isUser && suggestions && suggestions.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pl-1">
            {suggestions.map((q) => (
              <button
                key={q}
                onClick={() => onSuggest(q)}
                className="text-xs bg-white border border-violet-200 text-violet-700 rounded-full px-3 py-1.5 hover:bg-violet-50 hover:border-violet-400 transition-colors shadow-sm"
              >
                {q}
              </button>
            ))}
          </div>
        )}
      </div>

      {isUser && (
        <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 text-xs font-bold text-gray-600 shadow self-start mt-1">
          U
        </div>
      )}
    </div>
  );
}

function BotIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
    </svg>
  );
}

// ── Main widget ───────────────────────────────────────────────────────────────
export default function AIChatWidget() {
  const { user } = useAuth();
  const [isOpen, setIsOpen]           = useState(false);
  const [messages, setMessages]       = useState([]);
  const [suggestions, setSuggestions] = useState({});
  const [questionPool, setQuestionPool] = useState(GENERIC_QUESTIONS);
  const [input, setInput]             = useState('');
  const [isLoading, setIsLoading]     = useState(false);
  const [error, setError]             = useState('');
  const [hasGreeted, setHasGreeted]   = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef       = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  useEffect(() => {
    if (!isOpen) return;
    if (!hasGreeted) {
      setHasGreeted(true);
      const greeting = {
        role: 'assistant',
        content: `Hi${user?.name ? ' ' + user.name.split(' ')[0] : ''}! 👋 I'm your AI financial assistant.\n\nI know your portfolio — ask me anything about your holdings, income strategy, or dividend investing.`,
      };
      setMessages([greeting]);
      // Fetch holdings for personalised questions
      holdingsApi.getAll().then(res => {
        const holdings = res?.data?.data || [];
        const pool = buildPersonalisedQuestions(holdings);
        setQuestionPool(pool);
        setSuggestions({ 0: pickSuggestions(pool, 3) });
      }).catch(() => {
        setSuggestions({ 0: pickSuggestions(GENERIC_QUESTIONS, 3) });
      });
    }
    setTimeout(() => inputRef.current?.focus(), 300);
  }, [isOpen]);

  const sendMessage = useCallback(async (text) => {
    const content = (text || input).trim();
    if (!content || isLoading) return;

    setInput('');
    setError('');
    const newMessages = [...messages, { role: 'user', content }];
    setMessages(newMessages);
    setIsLoading(true);

    try {
      const res   = await aiApi.chat(newMessages);
      const reply = res?.data?.data?.reply || '';
      const aiMsg = { role: 'assistant', content: reply };
      setMessages(prev => {
        const updated = [...prev, aiMsg];
        // Pick 3 new suggestions, excluding recently asked questions
        const recentAsked = newMessages.filter(m => m.role === 'user').slice(-3).map(m => m.content);
        setSuggestions(prev => ({ ...prev, [updated.length - 1]: pickSuggestions(questionPool, 3, recentAsked) }));
        return updated;
      });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || 'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }, [input, messages, isLoading]);

  if (!['premium', 'pro', 'complimentary'].includes(user?.plan)) return null;

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const clearChat = () => {
    setMessages([]);
    setSuggestions({});
    setHasGreeted(false);
    setError('');
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 lg:hidden" onClick={() => setIsOpen(false)} />
      )}

      <div className={`fixed bottom-0 right-0 z-50 flex flex-col transition-all duration-300 ease-in-out
        ${isOpen
          ? 'w-full sm:w-[420px] h-[90vh] sm:h-[620px] sm:bottom-6 sm:right-6 sm:rounded-2xl shadow-2xl'
          : 'w-0 h-0 overflow-hidden pointer-events-none'
        }`}
      >
        {isOpen && (
          <div className="flex flex-col h-full bg-gray-50 sm:rounded-2xl overflow-hidden border border-gray-200 shadow-2xl">

            {/* Header */}
            <div className="bg-gradient-to-r from-violet-600 to-purple-700 px-4 py-3 flex items-center gap-3 flex-shrink-0">
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center shadow-inner">
                <BotIcon className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold text-sm leading-tight">AI Financial Assistant</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                  <p className="text-violet-200 text-xs">Portfolio-aware · Beta</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {messages.length > 1 && (
                  <button onClick={clearChat} className="p-1.5 rounded-lg hover:bg-white/20 text-violet-200 hover:text-white transition-colors" title="New chat">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                )}
                <button onClick={() => setIsOpen(false)} className="p-1.5 rounded-lg hover:bg-white/20 text-violet-200 hover:text-white transition-colors" title="Close">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4">
              {messages.map((msg, i) => (
                <div key={i} className="mb-3">
                  <Message
                    msg={msg}
                    suggestions={msg.role === 'assistant' && !isLoading ? suggestions[i] : null}
                    onSuggest={sendMessage}
                  />
                </div>
              ))}
              {isLoading && <TypingIndicator />}
              {error && (
                <div className="flex items-start gap-2 mb-4">
                  <div className="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="bg-red-50 border border-red-100 text-red-700 text-sm rounded-2xl rounded-bl-sm px-4 py-3">{error}</div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="px-4 py-3 bg-white border-t border-gray-100 flex-shrink-0">
              <div className="flex items-end gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus-within:border-violet-400 focus-within:ring-2 focus-within:ring-violet-100 transition-all">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask about your portfolio or investing..."
                  rows={1}
                  className="flex-1 bg-transparent text-sm text-gray-800 placeholder-gray-400 resize-none outline-none max-h-28 leading-relaxed"
                  disabled={isLoading}
                />
                <button
                  onClick={() => sendMessage()}
                  disabled={!input.trim() || isLoading}
                  className="w-8 h-8 flex-shrink-0 rounded-lg bg-gradient-to-br from-violet-600 to-purple-700 flex items-center justify-center text-white disabled:opacity-40 disabled:cursor-not-allowed hover:from-violet-700 hover:to-purple-800 transition-all shadow-sm"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </button>
              </div>
              <p className="text-center text-xs text-gray-400 mt-2">
                Powered by Gemini AI · General information only, not financial advice
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Floating button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-violet-600 to-purple-700 text-white shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all flex items-center justify-center"
          title="AI Financial Assistant"
        >
          <BotIcon className="w-6 h-6" />
          <span className="absolute inset-0 rounded-full bg-violet-500 animate-ping opacity-20" />
        </button>
      )}
    </>
  );
}
